import { useConfirm } from "@/components/DialogProvider";
import { scheduleToWorkerShifts } from "@/features/work-units/api/scheduleHelpers";
import { ClockInService } from "@/services/clockInService";
import { db } from "@/firebase";
import type { ProjectModel, WorkerModel } from "@/firestore_types";
import type { WorkerShift } from "@/lib/projectWorkerUtils";
import { formatTimeEST } from "@/lib/utils";
import { useLocation } from "@/hooks/useLocation";
import { usePostHogClockInIdentity } from "@/hooks/usePostHogClockInIdentity";
import { logException } from "@/logException";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { doc, onSnapshot, serverTimestamp, Timestamp, updateDoc } from "firebase/firestore";
import moment from "moment-timezone";
import { useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/clock-in/$clockInId/")({
  component: RouteComponent,
});

const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

// Helper to check if location coordinates are valid (not 0,0 or very close to 0)
const isValidLocation = (location: { lat: number; lng: number } | null): boolean => {
  if (!location) return false;
  const threshold = 0.0001;
  return Math.abs(location.lat) > threshold || Math.abs(location.lng) > threshold;
};

function RouteComponent() {
  const versionDisplay = (
    <div className="fixed top-1 left-1 z-50 text-[10px] text-gray-400 opacity-50">
      v{APP_VERSION}
    </div>
  );
  const { clockInId } = Route.useParams();
  const { clockIn, isLoading } = useClockIn(clockInId);
  const { worker } = useWorker(clockIn?.workerId);
  const { project } = useProject(clockIn?.projectId);
  const { location, locationError, locationLoading, locationSlow, locationVerySlow } =
    useLocation();

  usePostHogClockInIdentity(worker, clockIn, project);

  const [userClockedIn, setUserClockedIn] = useState(false);
  const [userClockedOut, setUserClockedOut] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);

  // Ref to prevent multiple timer schedules
  const clockInTimerScheduled = useRef(false);
  const clockOutTimerScheduled = useRef(false);

  const confirm = useConfirm();

  const getErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message : fallback;

  const clockInMutation = useMutation({
    mutationFn: async ({
      clockInData,
      locationData,
    }: {
      clockInData: ClockIn;
      locationData: { lat: number; lng: number };
    }) => {
      await ClockInService.clockIn({ clockIn: clockInData, location: locationData });
    },
    onMutate: () => setOperationError(null),
    onSuccess: () => {
      requestAnimationFrame(() => {
        setUserClockedIn(true);
      });
    },
    onError: (error) => {
      logException(error, {
        extra: { action: "clockIn", clockInId, workerId: clockIn?.workerId },
      });
      setOperationError(getErrorMessage(error, "Failed to clock in. Please try again."));
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: async ({
      clockInData,
      locationData,
    }: {
      clockInData: ClockIn;
      locationData: { lat: number; lng: number };
    }) => {
      await ClockInService.clockOut({ clockIn: clockInData, location: locationData });
    },
    onMutate: () => setOperationError(null),
    onSuccess: () => {
      requestAnimationFrame(() => {
        setUserClockedOut(true);
      });
    },
    onError: (error) => {
      logException(error, {
        extra: { action: "clockOut", clockInId, workerId: clockIn?.workerId },
      });
      setOperationError(getErrorMessage(error, "Failed to clock out. Please try again."));
    },
  });

  const startLunchMutation = useMutation({
    mutationFn: async () => {
      await ClockInService.startLunch(clockInId);
    },
    onMutate: () => setOperationError(null),
    onError: (error) => {
      logException(error, {
        extra: { action: "startLunch", clockInId, workerId: clockIn?.workerId },
      });
      setOperationError(getErrorMessage(error, "Failed to start lunch break. Please try again."));
    },
  });

  const endLunchMutation = useMutation({
    mutationFn: async () => {
      await ClockInService.endLunch(clockInId);
    },
    onMutate: () => setOperationError(null),
    onError: (error) => {
      logException(error, {
        extra: { action: "endLunch", clockInId, workerId: clockIn?.workerId },
      });
      setOperationError(getErrorMessage(error, "Failed to end lunch break. Please try again."));
    },
  });

  const isActionPending =
    clockInMutation.isPending ||
    clockOutMutation.isPending ||
    startLunchMutation.isPending ||
    endLunchMutation.isPending;

  const getActionLoadingMessage = () => {
    if (clockInMutation.isPending) return "Clocking in...";
    if (clockOutMutation.isPending) return "Clocking out...";
    if (startLunchMutation.isPending) return "Starting lunch break...";
    if (endLunchMutation.isPending) return "Ending lunch break...";
    return "Processing...";
  };

  const handleClockIn = async () => {
    if (!clockIn || !location) return;
    await clockInMutation.mutateAsync({ clockInData: clockIn, locationData: location });
  };

  const handleClockOut = async () => {
    if (!clockIn || !location) return;
    await clockOutMutation.mutateAsync({ clockInData: clockIn, locationData: location });
  };

  const handleStartLunch = async () => {
    await startLunchMutation.mutateAsync();
  };

  const handleEndLunch = async () => {
    await endLunchMutation.mutateAsync();
  };

  // Get the shifts from the work unit schedule
  // Priority: worker assignment schedule > work unit schedule > legacy workerShifts from clockIn
  const shifts = useMemo((): WorkerShift[] => {
    if (!clockIn || !project) {
      return [];
    }

    // Find the work unit for this clock-in
    // First try by workUnitId, then try to find by worker assignment
    let workUnit = clockIn.workUnitId
      ? project.workUnits?.find((wu) => wu.id === clockIn.workUnitId)
      : null;

    // If no workUnitId, try to find the work unit that has this worker assigned
    if (!workUnit) {
      workUnit =
        project.workUnits?.find((wu) =>
          wu.attachedWorkers?.some((w) => w.workerId === clockIn.workerId && !w.inactive),
        ) || null;
    }

    if (workUnit) {
      // Find the worker assignment in the work unit
      const workerAssignment = workUnit.attachedWorkers?.find(
        (w) => w.workerId === clockIn.workerId,
      );

      // Get the effective schedule: worker custom schedule > work unit schedule
      const schedule = workerAssignment?.schedule || workUnit.schedule || null;

      if (schedule) {
        return scheduleToWorkerShifts(schedule);
      }

      // Fall back to workerShifts from assignment if no schedule
      if (workerAssignment?.workerShifts) {
        return workerAssignment.workerShifts.filter(
          (shift): shift is WorkerShift => !!shift.start && !!shift.end,
        );
      }
    }

    // Fall back to legacy workerShifts from clockIn.assignedWorkers
    const legacyShifts =
      clockIn.assignedWorkers?.find((w) => w.workerId === clockIn.workerId)?.workerShifts || [];

    // Filter out incomplete shifts and cast to WorkerShift[]
    return legacyShifts.filter((shift): shift is WorkerShift => !!shift.start && !!shift.end);
  }, [clockIn, project]);

  // Auto-clear clock-in success message after 3 seconds
  useEffect(() => {
    if (userClockedIn && !clockInTimerScheduled.current) {
      clockInTimerScheduled.current = true;
      const timer = setTimeout(() => {
        setUserClockedIn(false);
        clockInTimerScheduled.current = false;
      }, 3000);
      return () => {
        clearTimeout(timer);
        clockInTimerScheduled.current = false;
      };
    }
  }, [userClockedIn]);

  // Auto-clear clock-out success message after 3 seconds
  useEffect(() => {
    if (userClockedOut && !clockOutTimerScheduled.current) {
      clockOutTimerScheduled.current = true;
      const timer = setTimeout(() => {
        setUserClockedOut(false);
        clockOutTimerScheduled.current = false;
      }, 3000);
      return () => {
        clearTimeout(timer);
        clockOutTimerScheduled.current = false;
      };
    }
  }, [userClockedOut]);

  // Track link opened timestamp
  useEffect(() => {
    if (clockIn && !clockIn.linkOpenedAt) {
      const clockInRef = doc(db, "clockIns", clockInId);
      updateDoc(clockInRef, {
        linkOpenedAt: serverTimestamp(),
      }).catch((error) => {
        console.error(error);
      });
    }
  }, [clockIn, clockInId]);

  // Real-time elapsed time tracking
  const [elapsedTime, setElapsedTime] = useState<string>("");

  useEffect(() => {
    if (!clockIn) return;

    let interval: NodeJS.Timeout;

    if (clockIn.clockedIn && !clockIn.clockedOut && clockIn.clockInAt) {
      // Set immediately so it shows without waiting 1 second
      const computeElapsed = () => {
        const now = new Date();
        const clockInTime = clockIn.clockInAt!.toDate();
        const diff = now.getTime() - clockInTime.getTime();
        setElapsedTime(formatDuration(diff));
      };
      computeElapsed();
      interval = setInterval(computeElapsed, 1000);
    } else if (
      clockIn.clockedIn &&
      clockIn.clockedOut &&
      clockIn.clockInAt &&
      clockIn.clockedOutAt
    ) {
      // Calculate total shift time
      const clockInTime = clockIn.clockInAt!.toDate();
      const clockOutTime = clockIn.clockedOutAt!.toDate();
      const diff = clockOutTime.getTime() - clockInTime.getTime();
      setElapsedTime(formatDuration(diff));
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [clockIn]);

  if (isLoading || locationLoading || isActionPending) {
    // Determine message based on loading state priority
    const loadingMessage = isActionPending
      ? getActionLoadingMessage()
      : locationLoading
        ? locationVerySlow
          ? "Still trying to get your location..."
          : locationSlow
            ? "Getting your location..."
            : "Getting your location..."
        : "Loading shift data...";

    return (
      <div className="flex min-h-screen items-center justify-center bg-white p-8">
        {versionDisplay}
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-green-500"></div>
          <p className="mb-4 text-gray-600">{loadingMessage}</p>

          {/* First tier hints - after 5 seconds */}
          {locationLoading && locationSlow && !locationVerySlow && (
            <div className="mt-6 rounded-lg border border-orange-200 bg-orange-50 p-4 text-left">
              <div className="flex items-start gap-2">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="mb-2 font-medium text-orange-800">
                    Location taking longer than usual?
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-orange-700">
                    <li>Move to an area with better GPS signal (near a window or outside)</li>
                    <li>Make sure location services are enabled</li>
                    <li>Stay on this page - we&apos;re still trying</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Second tier hints - after 15 seconds */}
          {locationLoading && locationVerySlow && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-left">
              <div className="flex items-start gap-2">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <div className="flex-1">
                  <p className="mb-2 font-medium text-red-800">
                    Having trouble finding your location
                  </p>
                  <p className="mb-3 text-sm text-red-700">
                    We&apos;re still trying, but here are some things that may help:
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-red-700">
                    <li>
                      <strong>Go outside</strong> or stand near a window
                    </li>
                    <li>
                      <strong>Check your phone settings</strong> - make sure Location Services is ON
                    </li>
                    <li>
                      <strong>Check browser permissions</strong> - allow location access for this
                      site
                    </li>
                    <li>
                      <strong>Try turning WiFi on</strong> - it can help with location accuracy
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-red-600">
                    Still waiting... If this doesn&apos;t work, try refreshing the page.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (locationError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-red-50 p-8">
        {versionDisplay}
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mb-4 text-lg font-semibold text-red-800">Location Access Required</h2>
          <div className="rounded-lg border border-red-200 bg-white p-4 text-left text-sm text-red-700">
            <pre className="font-sans whitespace-pre-wrap">{locationError}</pre>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-red-600 px-6 py-2 text-white transition-colors hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!location || !isValidLocation(location)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-orange-50 p-8">
        {versionDisplay}
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
            <svg
              className="h-8 w-8 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h2 className="mb-4 text-lg font-semibold text-orange-800">
            Unable to Get Your Location
          </h2>
          <div className="rounded-lg border border-orange-200 bg-white p-4 text-left text-sm text-orange-700">
            <p className="mb-3">
              We couldn&apos;t determine your exact location. This can happen when:
            </p>
            <ul className="mb-3 list-inside list-disc space-y-1">
              <li>You&apos;re indoors or in an area with poor GPS signal</li>
              <li>Your device&apos;s location services are having issues</li>
              <li>The location request timed out</li>
            </ul>
            <p className="font-medium">Please try the following:</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>Move to an area with better GPS reception (near a window or outside)</li>
              <li>Make sure location services are enabled on your device</li>
              <li>Try refreshing the page</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-orange-600 px-6 py-2 text-white transition-colors hover:bg-orange-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!clockIn) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        {versionDisplay}
        Invalid clock in link
      </div>
    );
  }

  if (userClockedIn) {
    return (
      <div className="bg-primary flex min-h-screen flex-col items-center justify-center text-white">
        {versionDisplay}
        <div className="animate-fade-in text-center">
          <div className="mb-6">
            <div className="animate-scale-in mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white">
              <svg
                className="text-primary h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
          </div>
          <p className="text-xl text-white md:text-2xl">Successfully clocked in</p>
        </div>
      </div>
    );
  }

  if (userClockedOut) {
    return (
      <div className="bg-primary flex min-h-screen flex-col items-center justify-center text-white">
        {versionDisplay}
        <div className="animate-fade-in text-center">
          <div className="mb-6">
            <div className="animate-scale-in mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white">
              <svg
                className="text-primary h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
          </div>
          <p className="text-xl text-white md:text-2xl">Successfully clocked out!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen justify-center bg-white">
      {versionDisplay}
      <div className="mx-auto flex max-w-md flex-col items-center p-8">
        {/* Company Logo */}
        <img src="/mainLogo.svg" alt="GetHandle" className="mt-6 mb-4 w-32 text-gray-800" />

        {/* Welcome Message */}
        <p className="text-md text-gray-600 opacity-75">
          Welcome{worker?.name ? `, ${worker.name}!` : "!"}
        </p>

        {/* Project Name and Address */}
        {project && (
          <div className="my-4 w-full rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">{project.name}</h3>
            {project.jobLocation && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <svg
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>
                  {typeof project.jobLocation === "string"
                    ? project.jobLocation
                    : project.jobLocation.address}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Operation Error Display */}
        {operationError && (
          <div className="my-4 w-full rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start">
              <svg
                className="mt-0.5 mr-2 h-5 w-5 flex-shrink-0 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-red-800">Operation Error</p>
                <p className="mt-1 text-sm text-red-600">{operationError}</p>
                <button
                  onClick={() => setOperationError(null)}
                  className="mt-2 text-xs text-red-700 underline hover:text-red-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Debug Information - Link Opened Timestamp */}
        {clockIn.linkOpenedAt && (
          <div className="my-3 w-full rounded-lg border border-gray-200 bg-gray-100 p-3">
            <h4 className="mb-1 text-xs font-medium text-gray-600">Link Information</h4>
            <div className="text-xs text-gray-500">
              <div>
                First opened:{" "}
                {clockIn.linkOpenedAt.toDate().toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}{" "}
                at{" "}
                {clockIn.linkOpenedAt.toDate().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>
          </div>
        )}

        {/* Assigned Work Times */}
        {shifts.length > 0 && (
          <div className="my-4 w-full rounded-lg bg-gray-50 p-4">
            <h3 className="mb-2 text-sm font-medium text-gray-700">Assigned Work Time</h3>
            {/* Schedule summary commented out for now - shows jumbled order */}
            {/* {effectiveSchedule && (
              <div className="mb-3 text-sm text-gray-600">
                {getScheduleSummary(effectiveSchedule)}
              </div>
            )} */}
            {[...shifts]
              .sort((a, b) => (a.start?.day || 0) - (b.start?.day || 0))
              .map((shift, index) => {
                const isShiftOnSameDay = shift.start?.day === shift.end?.day;
                const isCurrentShift =
                  shift.start?.day === getCurrentIsoDay() || shift.end?.day === getCurrentIsoDay();
                const startDay = daysOfWeek[(shift.start?.day || 1) - 1];
                const endDay = daysOfWeek[(shift.end?.day || 1) - 1];

                return (
                  <div
                    key={index}
                    className={`mb-3 rounded-lg border p-3 last:mb-0 ${isCurrentShift ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}
                  >
                    {isShiftOnSameDay ? (
                      // Single day shift
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`rounded px-2 py-1 text-xs font-medium ${isCurrentShift ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                          >
                            {startDay}
                          </div>
                          <div
                            className={`text-sm font-medium ${isCurrentShift ? "text-green-700" : "text-gray-700"}`}
                          >
                            {formatTimeEST(shift.start?.time)} - {formatTimeEST(shift.end?.time)}
                          </div>
                        </div>
                        {isCurrentShift && (
                          <div className="flex items-center text-green-600">
                            <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-green-500"></div>
                            <span className="text-xs font-medium">Today</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Two day shift
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                            Overnight Shift
                          </div>
                          {isCurrentShift && (
                            <div className="flex items-center text-green-600">
                              <div className="mr-2 h-2 w-2 animate-pulse rounded-full bg-green-500"></div>
                              <span className="text-xs font-medium">Active</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div
                              className={`rounded px-2 py-1 text-xs font-medium ${isCurrentShift && shift.start?.day === getCurrentIsoDay() ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                            >
                              {startDay}
                            </div>
                            <span
                              className={`text-sm font-medium ${isCurrentShift ? "text-green-700" : "text-gray-700"}`}
                            >
                              {formatTimeEST(shift.start?.time)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">Start</div>
                        </div>
                        <div className="flex items-center justify-center">
                          <div className="relative h-px w-full bg-gray-200">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform bg-white px-2">
                              <svg
                                className="h-4 w-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div
                              className={`rounded px-2 py-1 text-xs font-medium ${isCurrentShift && shift.end?.day === getCurrentIsoDay() ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                            >
                              {endDay}
                            </div>
                            <span
                              className={`text-sm font-medium ${isCurrentShift ? "text-green-700" : "text-gray-700"}`}
                            >
                              {formatTimeEST(shift.end?.time)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">End</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            {/* Show work unit name (work type) */}
            {(() => {
              const workUnit = clockIn.workUnitId
                ? project?.workUnits?.find((wu) => wu.id === clockIn.workUnitId)
                : null;
              const workType =
                workUnit?.name ||
                clockIn.assignedWorkers?.find((w) => w.workerId === clockIn.workerId)?.workType;
              return workType ? (
                <div className="mt-1 text-xs text-gray-500">Work Type: {workType}</div>
              ) : null;
            })()}
            <div className="mt-1 text-xs text-gray-500">
              Date:{" "}
              {clockIn.timestamp.toDate().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          </div>
        )}

        {/* Time Display */}
        {clockIn.clockedIn && elapsedTime && (
          <div className="my-4 w-full rounded-lg bg-blue-50 p-4">
            <h3 className="mb-2 text-sm font-medium text-blue-700">
              {clockIn.clockedOut ? "Total Shift Time" : "Elapsed Time"}
            </h3>
            <div className="text-2xl font-bold text-blue-800">{elapsedTime}</div>
            {clockIn.clockInAt && (
              <div className="mt-2 text-xs text-blue-600">
                <div>
                  Clocked in:{" "}
                  {clockIn.clockInAt.toDate().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  at{" "}
                  {clockIn.clockInAt.toDate().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </div>
              </div>
            )}
            {clockIn.clockedOut && clockIn.clockedOutAt && (
              <div className="mt-1 text-xs text-blue-600">
                <div>
                  Clocked out:{" "}
                  {clockIn.clockedOutAt.toDate().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  at{" "}
                  {clockIn.clockedOutAt.toDate().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lunch Break Summary - Show if lunch was taken */}
        {clockIn.lunchStartTime && clockIn.lunchEndTime && (
          <div className="my-4 w-full rounded-lg bg-orange-50 p-4">
            <h3 className="mb-2 text-sm font-medium text-orange-700">Lunch Break Taken</h3>
            <div className="text-sm text-orange-600">
              <div>
                Started:{" "}
                {clockIn.lunchStartTime.toDate().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                at{" "}
                {clockIn.lunchStartTime.toDate().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
              <div>
                Ended:{" "}
                {clockIn.lunchEndTime.toDate().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                at{" "}
                {clockIn.lunchEndTime.toDate().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
              <div className="mt-1 text-xs text-orange-500">
                Duration:{" "}
                {(() => {
                  const startTime = clockIn.lunchStartTime.toDate();
                  const endTime = clockIn.lunchEndTime.toDate();
                  const diff = endTime.getTime() - startTime.getTime();
                  const minutes = Math.floor(diff / (1000 * 60));
                  return `${minutes} minutes`;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Lunch Break Reminder */}
        {clockIn.onLunch && (
          <div className="my-4 w-full rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-center">
              <svg
                className="mr-2 h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <p className="font-medium text-yellow-800">Remember to resume after lunch break</p>
                <p className="mt-1 text-sm text-yellow-600">
                  Lunch started:{" "}
                  {clockIn.lunchStartTime?.toDate().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}{" "}
                  at{" "}
                  {clockIn.lunchStartTime?.toDate().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Message */}
        <p className="my-6 text-base text-gray-700">
          {!clockIn.clockedIn
            ? "You have not clocked in yet."
            : clockIn.onLunch
              ? "You are currently on lunch break."
              : clockIn.clockedIn && !clockIn.clockedOut
                ? "You are currently clocked in."
                : "You have completed your shift."}
        </p>

        {/* Action Buttons */}
        <div className="mt-4 w-full space-y-4">
          {!clockIn.clockedIn && (
            <>
              <button
                disabled={clockIn.clockedIn}
                className="bg-primary hover:bg-primary/80 w-full rounded-full px-8 py-4 text-lg font-semibold text-white transition-colors"
                onClick={async () => {
                  const confirmation = await confirm({
                    title: "Are you sure you want to clock in?",
                    description: "This will clock you in for the day.",
                    confirmText: "Clock In",
                    cancelText: "Cancel",
                  });

                  if (confirmation) {
                    await handleClockIn();
                  }
                }}
              >
                Clock In
              </button>
              <button
                disabled={true}
                className="w-full cursor-not-allowed rounded-full bg-gray-300 px-8 py-4 text-lg font-semibold text-black/30"
              >
                Clock Out
              </button>
            </>
          )}

          {/* Lunch Break Buttons - Only show when clocked in, not on lunch, and lunch not already taken */}
          {clockIn.clockedIn &&
            !clockIn.clockedOut &&
            !clockIn.onLunch &&
            !(clockIn.lunchStartTime && clockIn.lunchEndTime) && (
              <>
                <button
                  disabled={true}
                  className="w-full cursor-not-allowed rounded-full bg-gray-300 px-8 py-4 text-lg font-semibold text-black/30"
                >
                  Clock In
                </button>
                <button
                  className="w-full rounded-full bg-orange-500 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-orange-600"
                  onClick={async () => {
                    const confirmation = await confirm({
                      title: "Are you sure you want to start lunch break?",
                      description: "This will start your lunch break timer.",
                      confirmText: "Start Lunch",
                      cancelText: "Cancel",
                    });

                    if (confirmation) {
                      await handleStartLunch();
                    }
                  }}
                >
                  Start Lunch Break
                </button>
                <button
                  disabled={clockIn.clockedOut}
                  className="bg-primary hover:bg-primary/80 w-full rounded-full px-8 py-4 text-lg font-semibold text-white transition-colors"
                  onClick={async () => {
                    const confirmation = await confirm({
                      title: "Are you sure you want to clock out?",
                      description: "This will clock you out for the day.",
                      confirmText: "Clock Out",
                      cancelText: "Cancel",
                    });
                    if (confirmation) {
                      await handleClockOut();
                    }
                  }}
                >
                  Clock Out
                </button>
              </>
            )}

          {/* Resume from Lunch - Only show when on lunch */}
          {clockIn.onLunch && (
            <button
              className="w-full rounded-full bg-green-500 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-green-600"
              onClick={async () => {
                const confirmation = await confirm({
                  title: "Are you sure you want to end lunch break?",
                  description: "This will resume your work timer.",
                  confirmText: "Resume Work",
                  cancelText: "Cancel",
                });

                if (confirmation) {
                  await handleEndLunch();
                }
              }}
            >
              Resume from Lunch
            </button>
          )}

          {/* Clock Out Only - Show when lunch has been completed but still clocked in */}
          {clockIn.clockedIn &&
            !clockIn.clockedOut &&
            !clockIn.onLunch &&
            clockIn.lunchStartTime &&
            clockIn.lunchEndTime && (
              <>
                <button
                  disabled={true}
                  className="w-full cursor-not-allowed rounded-full bg-gray-300 px-8 py-4 text-lg font-semibold text-black/30"
                >
                  Clock In
                </button>
                <button
                  disabled={clockIn.clockedOut}
                  className="bg-primary hover:bg-primary/80 w-full rounded-full px-8 py-4 text-lg font-semibold text-white transition-colors"
                  onClick={async () => {
                    const confirmation = await confirm({
                      title: "Are you sure you want to clock out?",
                      description: "This will clock you out for the day.",
                      confirmText: "Clock Out",
                      cancelText: "Cancel",
                    });
                    if (confirmation) {
                      await handleClockOut();
                    }
                  }}
                >
                  Clock Out
                </button>
              </>
            )}
        </div>

        {/* Chat with Handle Team Button */}
        <Link to="/clock-in/$clockInId/chat" params={{ clockInId }} className="mt-4 block w-full">
          <button className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-green-600 px-8 py-4 text-lg font-semibold text-green-600 transition-colors hover:bg-green-50">
            💬 Chat with Handle Team
          </button>
        </Link>
      </div>
    </div>
  );
}

function getCurrentIsoDay() {
  const nowMoment = moment().tz("America/New_York");
  return nowMoment.isoWeekday();
}

function useClockIn(clockInId: string) {
  const [clockIn, setClockIn] = useState<ClockIn | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const clockInRef = doc(db, "clockIns", clockInId);
    const unsub = onSnapshot(
      clockInRef,
      (doc) => {
        const data = {
          clockInId: doc.id,
          ...doc.data(),
        } as ClockIn;
        setClockIn(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading clock-in data:", error);
        setIsLoading(false);
      },
    );
    return () => unsub();
  }, [clockInId]);
  return { clockIn, isLoading } as const;
}

function useWorker(workerId: string | undefined) {
  const [worker, setWorker] = useState<WorkerModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!workerId) {
      setIsLoading(false);
      return;
    }
    const workerRef = doc(db, "Workers", workerId);
    const unsub = onSnapshot(
      workerRef,
      (doc) => {
        const data = doc.data() as WorkerModel;
        setWorker(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading worker data:", error);
        setIsLoading(false);
      },
    );
    return () => unsub();
  }, [workerId]);
  return { worker, isLoading } as const;
}

function useProject(projectId: string | undefined) {
  const [project, setProject] = useState<ProjectModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }
    const projectRef = doc(db, "projects", projectId);
    const unsub = onSnapshot(
      projectRef,
      (doc) => {
        if (doc.exists()) {
          const data = { id: doc.id, ...doc.data() } as ProjectModel;
          setProject(data);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading project data:", error);
        setIsLoading(false);
      },
    );
    return () => unsub();
  }, [projectId]);
  return { project, isLoading } as const;
}

export interface ClockIn {
  clockInId: string;
  projectId: string;
  workerId: string;
  timestamp: Timestamp;
  clockedIn?: boolean;
  clockedOut?: boolean;
  clockInAt?: Timestamp;
  clockedOutAt?: Timestamp;
  customerId: string;
  assignedManagerIds?: string[]; // Array of manager IDs
  assignedWorkers: {
    workerId: string;
    workType: string;
    workerShifts: {
      end?: {
        day: number;
        time: string;
      };
      start?: {
        day: number;
        time: string;
      };
    }[];

    // startTime: string;
    // endTime: string;
  }[];
  lunchStartTime?: Timestamp;
  lunchEndTime?: Timestamp;
  onLunch?: boolean;
  linkOpenedAt?: Timestamp;
  markedSkipped?: boolean;
  markedSkippedNote?: string;
  workUnitId?: string; // NEW: Selected work unit for this clock-in
}
